import React, { useEffect, useRef } from "react"
import { useInfiniteQuery } from "@tanstack/react-query"
import { t } from "i18next"

import { v1FetchCompletedBookingActivities } from "@be-miniapp/api"
import { Button } from "@be-miniapp/ui/components/ui/button"
import { Spinner } from "@be-miniapp/ui/components/ui/spinner"
import { Typography } from "@be-miniapp/ui/components/ui/typography"
import { useInfiniteLoading } from "@be-miniapp/ui/hooks/use-infinite-loading"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"
import { useScrollRestoration } from "@be-miniapp/ui/hooks/use-scroll-restoration"

import { TransportActivityItem } from "./transport-item"

const ListTransportActivities = () => {
  const { data, isPending, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["transport-activities"],
    queryFn: async ({ pageParam }) => {
      return v1FetchCompletedBookingActivities({
        start_from: pageParam,
      })
    },
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage.history_size) return

      if (
        lastPage.data.length === 0 ||
        pages.length * 10 >= lastPage.history_size
      )
        return undefined

      return pages.length * 10
    },
    initialPageParam: 0,
  })

  const containerRef = useRef<HTMLDivElement | null>(null)

  const restore = useScrollRestoration({
    key: "transport-activities-transport",
  })

  const navigate = useNavigate()

  useEffect(() => {
    if (!containerRef?.current?.parentElement) return
    restore(containerRef?.current?.parentElement)
  }, [restore])

  const { ref } = useInfiniteLoading({
    onIntersect: fetchNextPage,
  })

  return (
    <div className="mt-3 flex w-full flex-col gap-3 py-2" ref={containerRef}>
      {isPending &&
        Array.from({ length: 8 }).map((_, idx) => (
          <TransportActivityItem.Skeleton key={idx} />
        ))}

      {data?.pages?.map((group, i) => (
        <React.Fragment key={i}>
          {group?.data?.map((item) => (
            <TransportActivityItem key={item.engagement_id} item={item} />
          ))}
        </React.Fragment>
      ))}

      {hasNextPage ? (
        <div ref={ref} className="flex h-8 w-full items-center justify-center">
          <Spinner />
        </div>
      ) : null}

      {data?.pages?.[0]?.data?.length === 0 ? (
        <div className="mt-12 flex flex-1 flex-col items-center justify-center gap-2">
          <img src="/images/21.webp" className="max-w-56 object-cover" />
          <Typography.Body
            variant="regular"
            size="medium"
            className="text-gray-800"
          >
            {t("activities.transport_detail.no_trip")}
          </Typography.Body>

          <Button
            onClick={() => {
              navigate("/ride/dropoff_location")
            }}
          >
            {t("transport_book_now")}
          </Button>
        </div>
      ) : (
        <div className="mb-safe-bottom h-4 w-full"></div>
      )}
    </div>
  )
}

export { ListTransportActivities }
